import { motion, useScroll, useTransform } from "framer-motion";
import { Phone } from "lucide-react";
import { scrollToTarget } from "../../lib/scroll";
import { EASE, Tag } from "../Reveal";
import { CTA } from "../CTA";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span className="block will-change-transform" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1.1, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "15+", label: "Years experience" },
  { value: "500+", label: "Projects completed" },
  { value: "100%", label: "Licensed & insured" },
  { value: "3", label: "States served" },
];

export const HomeHero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1200], [0, 140]);
  const scale = useTransform(scrollY, [0, 1200], [1, 1.12]);

  return (
    <section className="relative pt-28 sm:pt-40 overflow-hidden" data-testid="home-hero-section">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}>
          <Tag>15+ years of excellence in Delaware, PA, NJ & Baltimore</Tag>
        </motion.div>
        <h1 className="mt-8 font-display font-semibold text-ink tracking-[-0.03em] leading-[0.94] text-[clamp(2.9rem,8.4vw,9rem)]" data-testid="home-hero-headline">
          <MaskedLine delay={0.3}>Building trust</MaskedLine>
          <MaskedLine delay={0.4}>through <span className="text-teal">quality</span></MaskedLine>
          <MaskedLine delay={0.5}>craftsmanship.</MaskedLine>
        </h1>
        <motion.div className="mt-12 sm:mt-16 grid lg:grid-cols-12 gap-8 items-end" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.9, ease: EASE }}>
          <p className="lg:col-span-5 text-lg sm:text-xl text-ink/70 leading-snug max-w-md font-medium" data-testid="home-hero-subheadline">
            Your trusted partner for residential and light commercial construction. Licensed, insured, and committed to exceptional results.
          </p>
          <div className="lg:col-span-7 flex flex-wrap items-center gap-3 lg:justify-end">
            <CTA onClick={() => scrollToTarget("#estimate")} testId="home-hero-estimate-btn" className="min-w-[250px]">Request a free estimate</CTA>
            <button onClick={() => scrollToTarget("#projects")} data-testid="home-hero-projects-btn" className="inline-flex items-center gap-2 font-display font-medium text-sm sm:text-base text-ink px-6 py-4 border border-ink/20 hover:border-ink transition-colors duration-300">
              View our work
            </button>
            <a href="tel:3022301988" data-testid="home-hero-call-link" className="inline-flex items-center gap-2 font-display font-medium text-sm sm:text-base text-ink/70 hover:text-ink px-2 py-4 transition-colors">
              <Phone className="h-4 w-4" /> (302) 230-1988
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div className="relative mt-14 sm:mt-20 h-[62vh] sm:h-[78vh] overflow-hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.4, delay: 1.1 }}>
        <motion.img src="/images/home/hero.jpg" alt="Construction Alliance crew framing a residential project" className="absolute inset-0 h-full w-full object-cover" style={{ y, scale }} />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 bg-ink text-bone max-w-4xl divide-x divide-y sm:divide-y-0 divide-bone/15" data-testid="home-hero-stats">
              {STATS.map((s) => (
                <div key={s.label} className="px-4 sm:px-7 py-5 sm:py-7">
                  <p className="font-display font-semibold text-2xl sm:text-4xl tracking-[-0.02em]">{s.value}</p>
                  <p className="mt-1.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-bone/55">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
